export const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const escapeHtmlAttr = escapeHtml;

export const decodeHtml = (value) => {
  if (typeof window === 'undefined' || !value) {
    return String(value ?? '');
  }
  const el = document.createElement('textarea');
  el.innerHTML = value;
  return el.value;
};

export const WIDGET_CLASS = 'mc-widget';
export const WIDGET_ATTR = 'data-mc-widget';

const WIDGET_LABELS = {
  accordion: 'Accordion',
  flipcard: 'Flip card',
  timeline: 'Timeline',
  match: 'Match',
  'knowledge-check': 'Knowledge check',
};

const EDIT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>`;

const widgetChrome = (type) =>
  `<span class="mc-widget__chrome" contenteditable="false" data-mc-chrome>
    <span class="mc-widget__badge">${WIDGET_LABELS[type] || type}</span>
    <span class="mc-widget__edit" data-mc-edit role="button" tabindex="0" aria-label="Edit ${
      WIDGET_LABELS[type] || type
    }">${EDIT_ICON}</span>
  </span>`;

export const wrapWidget = (type, inner, extraAttrs = '') =>
  `<div class="${WIDGET_CLASS} mc-${type}" ${WIDGET_ATTR}="${type}" contenteditable="false"${
    extraAttrs ? ` ${extraAttrs}` : ''
  }>${widgetChrome(type)}${inner}</div>`;

export const accordionHtml = (panels = []) => {
  const items = panels
    .filter((panel) => panel.title || panel.body)
    .map(
      (panel, index) =>
        `<div class="mc-accordion__item${index === 0 ? ' is-open' : ''}">
          <div class="mc-accordion__header" data-mc-accordion-toggle>${escapeHtml(
            panel.title || 'Section',
          )}</div>
          <div class="mc-accordion__body">${escapeHtml(panel.body || '')}</div>
        </div>`,
    )
    .join('');
  return wrapWidget('accordion', items || '<div class="mc-accordion__item is-open"><div class="mc-accordion__header" data-mc-accordion-toggle>Section</div><div class="mc-accordion__body"></div></div>');
};

export const flipCardHtml = ({ front = '', back = '' } = {}) =>
  wrapWidget(
    'flipcard',
    `<div class="mc-flipcard__inner">
      <div class="mc-flipcard__face mc-flipcard__front">${escapeHtml(front)}</div>
      <div class="mc-flipcard__face mc-flipcard__back">${escapeHtml(back)}</div>
    </div>
    <span class="mc-flipcard__hint">Click to flip</span>`,
  );

export const timelineHtml = (steps = []) => {
  const items = steps
    .filter((step) => step.title || step.body)
    .map(
      (step) =>
        `<div class="mc-timeline__item">
          <div class="mc-timeline__marker"></div>
          <div class="mc-timeline__content">
            <div class="mc-timeline__title">${escapeHtml(step.title || '')}</div>
            <div class="mc-timeline__body">${escapeHtml(step.body || '')}</div>
          </div>
        </div>`,
    )
    .join('');
  return wrapWidget('timeline', items);
};

export const matchHtml = (pairs = []) => {
  const valid = pairs.filter((pair) => pair.term || pair.definition);
  const payload = JSON.stringify(valid);
  const terms = valid
    .map(
      (pair, index) =>
        `<div class="mc-match__term" data-match-id="${index}">${escapeHtml(
          pair.term || '',
        )}</div>`,
    )
    .join('');
  const defs = valid
    .map(
      (pair, index) =>
        `<div class="mc-match__def" draggable="true" data-match-id="${index}">${escapeHtml(
          pair.definition || '',
        )}</div>`,
    )
    .join('');
  return wrapWidget(
    'match',
    `<div class="mc-match__col mc-match__terms">${terms}</div>
     <div class="mc-match__col mc-match__defs">${defs}</div>
     <p class="mc-match__hint">Drag each definition next to its matching term.</p>
     <span class="mc-match__pairs" hidden>${escapeHtml(payload)}</span>`,
    `data-mc-pairs="${escapeHtmlAttr(payload)}"`,
  );
};

export const knowledgeCheckHtml = (questionIds = []) => {
  const ids = questionIds.filter(Boolean).join(',');
  const count = questionIds.filter(Boolean).length;
  return wrapWidget(
    'knowledge-check',
    `<div class="mc-knowledge-check__placeholder">
      <strong>Knowledge check</strong>
      <span>${count} question${count === 1 ? '' : 's'}</span>
    </div>
    <span class="mc-knowledge-check__ids" hidden>${escapeHtml(ids)}</span>`,
    `data-question-ids="${escapeHtmlAttr(ids)}"`,
  );
};

export const readAccordionPanels = (node) =>
  [...(node?.querySelectorAll('.mc-accordion__item') || [])].map((item) => ({
    title: item.querySelector('.mc-accordion__header')?.textContent?.trim() || '',
    body: item.querySelector('.mc-accordion__body')?.textContent?.trim() || '',
  }));

export const readFlipCard = (node) => ({
  front: node?.querySelector('.mc-flipcard__front')?.textContent?.trim() || '',
  back: node?.querySelector('.mc-flipcard__back')?.textContent?.trim() || '',
});

export const readTimelineSteps = (node) =>
  [...(node?.querySelectorAll('.mc-timeline__item') || [])].map((item) => ({
    title: item.querySelector('.mc-timeline__title')?.textContent?.trim() || '',
    body: item.querySelector('.mc-timeline__body')?.textContent?.trim() || '',
  }));

export const readMatchPairs = (node) => {
  const parsePairs = (raw) => {
    try {
      const parsed = JSON.parse(decodeHtml(raw));
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  };
  const fromAttr = parsePairs(node?.getAttribute('data-mc-pairs') || '');
  if (fromAttr.length) {
    return fromAttr;
  }
  return parsePairs(
    node?.querySelector('.mc-match__pairs')?.textContent || '',
  );
};

export const readKnowledgeCheckIds = (node) => {
  const fromAttr = String(node?.getAttribute('data-question-ids') || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
  if (fromAttr.length) {
    return fromAttr;
  }
  return String(node?.querySelector('.mc-knowledge-check__ids')?.textContent || '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

export const extractKnowledgeCheckIds = (html) => {
  if (typeof html !== 'string' || !html) {
    return [];
  }
  const ids = [];
  const addIds = (value) => {
    String(value)
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .forEach((id) => {
        if (!ids.includes(id)) {
          ids.push(id);
        }
      });
  };
  const attrRegex = /data-question-ids="([^"]*)"/gi;
  let match = attrRegex.exec(html);
  while (match) {
    addIds(match[1]);
    match = attrRegex.exec(html);
  }
  const spanRegex = /mc-knowledge-check__ids[^>]*>([^<]*)/gi;
  match = spanRegex.exec(html);
  while (match) {
    addIds(match[1]);
    match = spanRegex.exec(html);
  }
  return ids;
};

export const replaceWidgetNode = (editorInstance, existingNode, html) => {
  if (existingNode && existingNode.parentNode) {
    existingNode.outerHTML = html;
    editorInstance.synchronizeValues();
    return;
  }
  editorInstance.s.insertHTML(`${html}<p><br></p>`);
};
